import { create } from 'zustand';
import { endpoints, type ServerConfig, type User } from '../services/api';

export interface ProviderStatus {
  connected: boolean;
  email: string | null;
}

interface SessionStore {
  config: ServerConfig | null;
  user: User | null;
  loaded: boolean;
  microsoft: ProviderStatus | null;
  load: () => Promise<void>;
  setUser: (u: User | null) => void;
  refreshMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
}

const offlineConfig: ServerConfig = {
  env: 'offline',
  features: { accounts: false, billing: false, microsoft: false },
  licensePrice: { amount: 1000, currency: 'GBP' },
};

export const useSession = create<SessionStore>()((set, get) => ({
  config: null,
  user: null,
  loaded: false,
  microsoft: null,
  load: async () => {
    const [config, me] = await Promise.allSettled([endpoints.config(), endpoints.me()]);
    set({
      config: config.status === 'fulfilled' ? config.value : offlineConfig,
      user: me.status === 'fulfilled' ? me.value.user : null,
      loaded: true,
    });
    await get().refreshMicrosoft();
  },
  setUser: (user) => {
    set({ user });
    void get().refreshMicrosoft();
  },
  refreshMicrosoft: async () => {
    const { user, config } = get();
    if (!user || !config?.features.microsoft) {
      set({ microsoft: null });
      return;
    }
    try {
      set({ microsoft: await endpoints.microsoftStatus() });
    } catch {
      set({ microsoft: null });
    }
  },
  signOut: async () => {
    await endpoints.logout().catch(() => undefined);
    set({ user: null, microsoft: null });
  },
}));

export const isLicensed = (u: User | null): boolean => u?.license === 'lifetime';

/** Starts the Microsoft OAuth flow (full-page redirect). `returnTo` is an app path, e.g. /pair. */
export function startMicrosoft(mode: 'signin' | 'link', returnTo?: string): void {
  const q = new URLSearchParams({ mode });
  if (returnTo) q.set('returnTo', returnTo);
  window.location.assign(`/api/auth/oauth/microsoft/start?${q}`);
}
